import React from 'react';
import { useParams, Link } from 'react-router-dom';

const RecipeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const recipes = {
    'ghormeh-sabzi': {
      name: 'Ghormeh Sabzi',
      persianName: 'قورمه سبزی',
      description: 'A fragrant herb stew with tender lamb, kidney beans, and dried limes',
      difficulty: 'medium',
      cookingTime: '2 hours',
      servings: 6,
      calories: 320,
      image: '🌿',
      ingredients: [
        { name: 'lamb shoulder', amount: '1 lb', icon: '🥩' },
        { name: 'fresh herbs (parsley, cilantro, dill)', amount: '2 cups', icon: '🌿' },
        { name: 'kidney beans', amount: '1 can', icon: '🫘' },
        { name: 'dried limes', amount: '3-4', icon: '🍋' },
        { name: 'onions', amount: '2 large', icon: '🧅' },
        { name: 'turmeric', amount: '1 tsp', icon: '🌶️' },
        { name: 'salt and pepper', amount: 'to taste', icon: '🧂' }
      ],
      instructions: [
        'Chop the fresh herbs finely and sauté until fragrant',
        'Brown the lamb pieces in oil with onions',
        'Add turmeric, salt, and pepper to the meat',
        'Add the herbs and dried limes',
        'Pour in water and simmer for 1.5 hours',
        'Add kidney beans and cook for 30 more minutes',
        'Serve hot with Persian rice'
      ],
      culturalNote: 'Ghormeh Sabzi is considered the national dish of Iran and is often served at important gatherings and celebrations.',
      nutritionTip: 'This dish is rich in iron from the lamb and herbs, and provides a good source of protein and fiber.'
    },
    'fesenjan': {
      name: 'Fesenjan',
      persianName: 'فسنجان',
      description: 'Rich pomegranate and walnut stew with tender chicken or duck',
      difficulty: 'hard',
      cookingTime: '3 hours',
      servings: 4,
      calories: 450,
      image: '🍗',
      ingredients: [
        { name: 'chicken or duck', amount: '1 whole', icon: '🍗' },
        { name: 'walnuts', amount: '2 cups', icon: '🌰' },
        { name: 'pomegranate molasses', amount: '1/2 cup', icon: '🍎' },
        { name: 'onions', amount: '2 large', icon: '🧅' },
        { name: 'sugar', amount: '2 tbsp', icon: '🍯' },
        { name: 'saffron', amount: '1/4 tsp', icon: '🌶️' },
        { name: 'salt and pepper', amount: 'to taste', icon: '🧂' }
      ],
      instructions: [
        'Grind walnuts to a fine paste',
        'Brown the chicken pieces in oil',
        'Sauté onions until golden',
        'Add walnut paste and pomegranate molasses',
        'Add chicken and simmer for 2 hours',
        'Adjust sweetness with sugar',
        'Serve with saffron rice and barberries'
      ],
      culturalNote: 'Fesenjan is traditionally served during the winter months and is considered a luxury dish often prepared for special occasions.',
      nutritionTip: 'Walnuts provide healthy omega-3 fats, while pomegranate offers antioxidants and vitamin C.'
    },
    'chelo-kabab': {
      name: 'Chelo Kabab',
      persianName: 'چلو کباب',
      description: 'Grilled meat kebabs served with saffron rice and grilled tomatoes',
      difficulty: 'medium',
      cookingTime: '1.5 hours',
      servings: 4,
      calories: 380,
      image: '🍖',
      ingredients: [
        { name: 'lamb or beef', amount: '1 lb', icon: '🥩' },
        { name: 'saffron rice', amount: '2 cups', icon: '🍚' },
        { name: 'tomatoes', amount: '4 large', icon: '🍅' },
        { name: 'onions', amount: '2 large', icon: '🧅' },
        { name: 'saffron', amount: '1/2 tsp', icon: '🌶️' },
        { name: 'yogurt', amount: '1 cup', icon: '🥛' },
        { name: 'sumac', amount: '2 tbsp', icon: '🌶️' }
      ],
      instructions: [
        'Marinate meat with onions, yogurt, and spices',
        'Prepare saffron rice with crispy bottom (tahdig)',
        'Form meat into kebabs on skewers',
        'Grill kebabs until charred and cooked through',
        'Grill tomatoes and onions',
        'Serve with rice, grilled vegetables, and sumac',
        'Accompany with fresh herbs and yogurt'
      ],
      culturalNote: 'Chelo Kabab is one of the most popular Persian dishes and is often served in restaurants and at family gatherings.',
      nutritionTip: 'This dish provides a good balance of protein, carbohydrates, and healthy fats from the meat and rice.'
    },
    'tahchin': {
      name: 'Tahchin',
      persianName: 'ته چین',
      description: 'Layered saffron rice cake with chicken, yogurt, and barberries',
      difficulty: 'hard',
      cookingTime: '2.5 hours',
      servings: 6,
      calories: 420,
      image: '🍚',
      ingredients: [
        { name: 'chicken', amount: '1 lb', icon: '🍗' },
        { name: 'basmati rice', amount: '3 cups', icon: '🍚' },
        { name: 'yogurt', amount: '2 cups', icon: '🥛' },
        { name: 'saffron', amount: '1 tsp', icon: '🌶️' },
        { name: 'barberries', amount: '1/2 cup', icon: '🫐' },
        { name: 'eggs', amount: '4', icon: '🥚' },
        { name: 'butter', amount: '1/2 cup', icon: '🧈' }
      ],
      instructions: [
        'Cook chicken with spices until tender',
        'Parboil rice and drain',
        'Mix yogurt, eggs, and saffron',
        'Layer rice and chicken in a pot',
        'Add barberries between layers',
        'Cook on low heat for 1 hour',
        'Invert to reveal the golden crust'
      ],
      culturalNote: 'Tahchin is often served at weddings and celebrations, symbolizing prosperity and abundance with its golden crust.',
      nutritionTip: 'The combination of rice and chicken provides complete protein, while saffron offers anti-inflammatory properties.'
    },
    'ash-reshteh': {
      name: 'Ash Reshteh',
      persianName: 'آش رشته',
      description: 'Hearty noodle soup with beans, herbs, and kashk (whey)',
      difficulty: 'easy',
      cookingTime: '1 hour',
      servings: 8,
      calories: 280,
      image: '🍜',
      ingredients: [
        { name: 'reshteh noodles', amount: '1 lb', icon: '🍜' },
        { name: 'chickpeas', amount: '1 can', icon: '🫘' },
        { name: 'kidney beans', amount: '1 can', icon: '🫘' },
        { name: 'fresh herbs', amount: '2 cups', icon: '🌿' },
        { name: 'kashk (whey)', amount: '1 cup', icon: '🥛' },
        { name: 'onions', amount: '2 large', icon: '🧅' },
        { name: 'mint', amount: '1/4 cup', icon: '🌿' }
      ],
      instructions: [
        'Sauté onions until golden',
        'Add beans and herbs to the pot',
        'Pour in water and bring to boil',
        'Add noodles and cook until tender',
        'Stir in kashk and adjust seasoning',
        'Garnish with fried onions and mint',
        'Serve hot with bread'
      ],
      culturalNote: 'Ash Reshteh is traditionally served on the 13th day of Nowruz (Persian New Year) and is believed to bring good fortune.',
      nutritionTip: 'This soup is high in fiber from beans and herbs, and provides a good source of plant-based protein.'
    }
  };

  const recipe = recipes[id as keyof typeof recipes];

  if (!recipe) {
    return (
      <div className="min-h-screen bg-persian-bg py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-elegant text-persian-saffron mb-4">Recipe Not Found</h1>
          <p className="text-persian-text-alt mb-8">The recipe you're looking for doesn't exist.</p>
          <Link to="/cultural/recipes" className="btn-persian-primary">
            Back to Recipes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-persian-bg py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link 
            to="/cultural/recipes" 
            className="inline-flex items-center text-persian-saffron hover:text-persian-gold transition-colors"
          >
            <span className="mr-2">←</span>
            Back to Recipes
          </Link>
        </div>

        {/* Recipe Header */}
        <div className="recipe-card mb-8">
          <div className="text-center mb-8">
            <div className="text-8xl mb-6">{recipe.image}</div>
            <h1 className="text-4xl md:text-5xl font-elegant text-persian-saffron mb-4">
              {recipe.name}
            </h1>
            <h2 className="text-2xl text-persian-sky mb-6 font-farsi">
              {recipe.persianName}
            </h2>
            <p className="text-lg text-persian-text-alt leading-relaxed max-w-2xl mx-auto">
              {recipe.description}
            </p>
          </div>

          {/* Recipe Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="text-center p-4 bg-persian-card-alt rounded-soft">
              <div className="cooking-time text-lg font-semibold">
                <span>{recipe.cookingTime}</span>
              </div>
              <div className="text-persian-text-alt text-sm mt-1">Cooking Time</div>
            </div>
            <div className="text-center p-4 bg-persian-card-alt rounded-soft">
              <div className="servings text-lg font-semibold">
                <span>{recipe.servings} servings</span>
              </div>
              <div className="text-persian-text-alt text-sm mt-1">Servings</div>
            </div>
            <div className="text-center p-4 bg-persian-card-alt rounded-soft">
              <div className="text-lg font-semibold text-persian-saffron">
                {recipe.difficulty.charAt(0).toUpperCase() + recipe.difficulty.slice(1)}
              </div>
              <div className="text-persian-text-alt text-sm mt-1">Difficulty</div>
            </div>
            <div className="text-center p-4 bg-persian-card-alt rounded-soft">
              <div className="text-lg font-semibold text-persian-mint">
                {recipe.calories} cal
              </div>
              <div className="text-persian-text-alt text-sm mt-1">Per Serving</div>
            </div>
          </div>
        </div>

        {/* Ingredients Section */}
        <div className="recipe-card mb-8">
          <h3 className="section-title">Ingredients</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recipe.ingredients.map((ingredient, index) => (
              <div 
                key={index}
                className="ingredient-item"
                style={{ '--ingredient-icon': `"${ingredient.icon}"` } as React.CSSProperties}
              >
                <span className="text-persian-sky text-xl mr-3">
                  {ingredient.icon}
                </span>
                <div className="flex-1">
                  <div className="text-persian-text font-medium capitalize">
                    {ingredient.name}
                  </div>
                  <div className="text-persian-text-alt text-sm">
                    {ingredient.amount}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions Section */}
        <div className="recipe-card mb-8">
          <h3 className="section-title">Instructions</h3>
          <div className="space-y-4">
            {recipe.instructions.map((instruction, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-persian-saffron to-persian-gold rounded-full flex items-center justify-center text-persian-bg font-bold text-sm">
                  {index + 1}
                </div>
                <p className="text-persian-text-alt leading-relaxed">
                  {instruction}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Cultural Note */}
        <div className="cultural-note mb-8">
          <h4 className="text-lg font-elegant text-persian-rose mb-3">
            🏛️ Cultural Significance
          </h4>
          <p className="text-persian-text-alt">
            {recipe.culturalNote}
          </p>
        </div>

        {/* Nutrition Tip */}
        <div className="nutrition-tip mb-8">
          <h4 className="text-lg font-semibold text-persian-mint mb-3">
            💚 Nutrition Tip
          </h4>
          <p className="text-persian-text-alt">
            {recipe.nutritionTip}
          </p>
        </div>

        {/* Serving Suggestions */}
        <div className="recipe-card">
          <h3 className="section-title">Serving Suggestions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-semibold text-persian-saffron mb-3">Traditional Accompaniments</h4>
              <ul className="space-y-2 text-persian-text-alt">
                <li className="flex items-center">
                  <span className="text-persian-mint mr-2">✓</span>
                  Persian rice with saffron
                </li>
                <li className="flex items-center">
                  <span className="text-persian-mint mr-2">✓</span>
                  Fresh herbs (mint, basil, tarragon)
                </li>
                <li className="flex items-center">
                  <span className="text-persian-mint mr-2">✓</span>
                  Grilled vegetables
                </li>
                <li className="flex items-center">
                  <span className="text-persian-mint mr-2">✓</span>
                  Yogurt with cucumber
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-persian-saffron mb-3">Beverage Pairings</h4>
              <ul className="space-y-2 text-persian-text-alt">
                <li className="flex items-center">
                  <span className="text-persian-sky mr-2">☕</span>
                  Persian tea with cardamom
                </li>
                <li className="flex items-center">
                  <span className="text-persian-sky mr-2">🥤</span>
                  Doogh (yogurt drink)
                </li>
                <li className="flex items-center">
                  <span className="text-persian-sky mr-2">🍷</span>
                  Red wine (for meat dishes)
                </li>
                <li className="flex items-center">
                  <span className="text-persian-sky mr-2">💧</span>
                  Sparkling water with lime
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailPage; 